import '../../../../components/Functions/CodeBlock.scss';
import './ReportLogicList.scss';
import ColumnDefinition from '../../../../infrastructure/types/DataGrid/ColumnDefinition';
import React, { Fragment } from 'react';
import UiDataGrid from '../../../Common/DataGrid/UiDataGrid/UiDataGrid';
import { generateKey } from '../../../../infrastructure/helpers/react/commonHelper';
import DropZone from '../../../Common/Dragging/DropZone/DropZone';
import { ReactComponent as CaretLeft } from '../../../Common/BootstrapIcons/caret-left-fill.svg';
import { ReactComponent as CaretRight } from '../../../Common/BootstrapIcons/caret-right-fill.svg';
import copyArray from '../../../../infrastructure/helpers/common/copyArray';
import NameAndNumber from '../../../../types/report/NameAndNumber';
import AutocompleteInput from '../../../Common/AutocompleteInput/SearchByFor';
import domNavigationHelper from '../../../../infrastructure/helpers/html/domNavigationHelper';
import store from '../../../../store/store';
import actions from '../../../../store/actions';

interface ReportLogicListProps<T> {
    isWaiting: boolean;
    items: T[];
    columns: ColumnDefinition<T>[];
    getContent: (item: T) => JSX.Element;
    getKey: (item: T) => number;
    createHtmlId: (item: T) => string;
    isPagingUpdate: boolean;
    navigateTo: string;
    showLastPage: boolean;
    itemsPerPage?: number;
    onClick?: (e: React.MouseEvent<HTMLElement>, item: T) => void;
    onDropZoneDrop?: (dragIdentifier: number, dropIdentifier: number) => void;
}

interface State<T> {
    currentPage: number;
    lastPage: number;
    searchValue: string;
    items: T[];
    previousPageButtonDisabled: boolean;
    nextPageButtonDisabled: boolean;
    isPagingUpdate: boolean;
}

const defaultItemsAmountPerPage = 10;

class ReportLogicListWithPaging<T> extends React.Component<ReportLogicListProps<T>, State<T>> {

    constructor(props: ReportLogicListProps<T>) {
        super(props);

        this.state = {
            currentPage: 1,
            items: [],
            lastPage: 1,
            searchValue: '',
            nextPageButtonDisabled: true,
            previousPageButtonDisabled: true,
            isPagingUpdate: false,
        }

        this.onDropZoneDrop = this.onDropZoneDrop.bind(this);
        this.onPreviousClick = this.onPreviousClick.bind(this);
        this.onNextClick = this.onNextClick.bind(this);
    }

    componentDidMount() {
        let lastPage = Math.ceil(this.props.items.length / (this.props.itemsPerPage || defaultItemsAmountPerPage));

        let items = copyArray(this.props.items).slice(0, this.props.itemsPerPage || defaultItemsAmountPerPage);

        let isNextPageDisable = lastPage === this.state.currentPage;

        this.setState({ lastPage, items, nextPageButtonDisabled: isNextPageDisable });
    }

    componentDidUpdate(prevProps: ReportLogicListProps<T>) {
        if (((prevProps.items !== this.props.items || prevProps.items.length !== this.props.items.length)) ||
            (!this.state.isPagingUpdate && this.props.isPagingUpdate)) {

            let lastPage = Math.ceil(this.props.items.length / (this.props.itemsPerPage || defaultItemsAmountPerPage));

            let currentPage = this.props.showLastPage ? lastPage : this.state.currentPage;

            if (lastPage < currentPage) {
                currentPage = lastPage;
            }

            if (lastPage > 0 && currentPage === 0) {
                currentPage = 1;
            }

            let isPreviousPageDisable = currentPage < 2;

            let isNextPageDisable = lastPage === currentPage;

            let startIndex = (currentPage - 1) * (this.props.itemsPerPage || defaultItemsAmountPerPage);

            let items = copyArray(this.props.items).slice(startIndex, startIndex + (this.props.itemsPerPage || defaultItemsAmountPerPage));

            this.setState({ lastPage, items, previousPageButtonDisabled: isPreviousPageDisable, nextPageButtonDisabled: isNextPageDisable, currentPage, isPagingUpdate: this.props.isPagingUpdate || false }, () => {
                if (this.props.navigateTo) {

                    domNavigationHelper.navigate(this.props.navigateTo);

                    store.dispatch({ type: actions.reference.navigated, payload: {} });
                }
            });
        }
    }

    onDropZoneDrop(dropIndex: number, e: React.DragEvent<HTMLDivElement>): void {

        try {
            const dragNumber = parseInt(JSON.parse(e.dataTransfer.getData('transferData'))['dragIdentifier']);

            this.props.onDropZoneDrop && this.props.onDropZoneDrop(dragNumber, dropIndex + 1);
        }
        catch (error) { }
    }

    requestPage(currentPage: number): void {

        this.setState({ currentPage });

        let startIndex = (currentPage - 1) * (this.props.itemsPerPage || defaultItemsAmountPerPage)

        let items = copyArray(this.props.items).slice(startIndex, startIndex + (this.props.itemsPerPage || defaultItemsAmountPerPage));

        this.setState({ items });
    }

    onPreviousClick(): void {
        let page = this.state.currentPage - 1;

        if (page > 0) {
            this.setState({ previousPageButtonDisabled: page === 1, nextPageButtonDisabled: false });

            this.requestPage(page);
        }
    }

    onNextClick(): void {

        let page = this.state.currentPage + 1;

        this.setState({ nextPageButtonDisabled: page === this.state.lastPage, previousPageButtonDisabled: false });

        if (page <= this.state.lastPage) {
            this.requestPage(page);
        }
    }

    render(): JSX.Element {

        if (this.props.isWaiting) {
            return (
                <></>
            );
        }

        return (
            <div className="report-logic">
                <div className='paging-container'>
                        <button
                            type="button"
                            className="transparent-button caret-button"
                            title="Previous Page"
                            disabled={this.state.previousPageButtonDisabled}
                            onClick={() => this.onPreviousClick()}>
                            <CaretLeft />
                        </button>

                        <span className="page-indicator">{this.state.currentPage} of {this.state.lastPage}</span>

                        <button
                            type="button"
                            className="transparent-button caret-button"
                            title="Next Page"
                            disabled={this.state.nextPageButtonDisabled}
                            onClick={() => this.onNextClick()}>
                            <CaretRight />
                        </button>
                </div>
                {this.state.items.map((item, index) => {
                    return (
                        <Fragment key={this.props.createHtmlId(item)} >
                            <div className="item" key={this.props.createHtmlId(item)} id={this.props.createHtmlId(item)}
                                onClick={(e) => { this.props.onClick && this.props.onClick(e, item) }}>

                                <UiDataGrid key={this.props.createHtmlId(item)} columns={this.props.columns} data={[item]} getKey={(x, i) => i} />

                                {this.props.getContent(item)}
                            </div>
                            <DropZone key={generateKey()} className='height-10px drop-box'
                                onDrop={(e) => this.onDropZoneDrop(index, e)} />
                        </Fragment>
                    );
                })
                }
            </div>
        );
    }
}

export default ReportLogicListWithPaging;